import { Injectable } from '@angular/core';
import {catchError, map, Observable, of, timeout} from "rxjs";
import { ApiService } from './api.service';
import { HttpClient } from '@angular/common/http';
import { PersistencyService } from './persistency.service';
import { School, SchoolUser, SyncMode, UserProfile } from '@core/models/domain/account.domain';
import { AddUserRequest, ModifyUserRequest, ModifyUserProfileRequest, ModifySchoolRequest, UpdateSchoolContactRequest, UpdateSchoolLogoRequest } from '@core/models/request/account.request';
import { buildSchoolFromIntegrationResponse, buildSchoolFromResponse, buildSchoolUserFromResponse, buildUserProfileFromRequest, toSyncMode } from '@core/converter/account.converter';
import { SchoolIntegrationResponse, SchoolResponse, SchoolSyncStatusResponse, SchoolUserInfoResponse, UserInfoResponse } from '@core/models/response/account.response';
import { DigiResponse } from '@core/models/response/core';
import { buildApiEndpoint, handleError } from '@core/utils/service';

/**
 * AccountService
 *
 * This service provides methods for managing users and school information, including loading, adding, updating, and removing users.
 * It facilitates communication with the backend for user-related and school-related operations.
 *
 * Key Features:
 * - Loads all users associated with the school account.
 * - Adds new users by sending requests to the server.
 * - Updates existing users by sending modification requests to the server.
 * - Removes users by sending delete requests to the server.
 * - Manages school-related information.
 */

@Injectable({
  providedIn: 'root'
})
export class AccountService {
  static readonly LOAD_TIMEOUT_MS = 30_000;

  SCHOOL_API_URI: string;
  ACCOUNT_API_URI: string;
  INTEGRATION_API_URI: string;

  constructor(private http: HttpClient, private apiService: ApiService, private persistencyService: PersistencyService) {
    this.SCHOOL_API_URI = this.apiService.getApiURL("UserService", "school-account");
    this.ACCOUNT_API_URI = this.apiService.getApiURL("UserService", "account");
    this.INTEGRATION_API_URI = this.apiService.getApiURL("IntegrationService", "schools");
  }

  /**
   * Loads all users by sending a GET request to the server.
   *
   * This function retrieves a list of users from the server by making a GET request to the `get-users` endpoint.
   * The response is mapped to an array of `SchoolUser` objects using the `buildSchoolUserFromResponse` converter.
   * Logs a warning if the response body is invalid or not an array, and returns an empty array in such cases.
   *
   * @returns {Observable<SchoolUser[]>} An observable that emits an array of `SchoolUser` objects or an empty array in case of an invalid response.
   */
  loadUsers(): Observable<SchoolUser[]> {
    return this.http
      .get<DigiResponse<SchoolUserInfoResponse[]>>(buildApiEndpoint(this.SCHOOL_API_URI, "get-users"), { observe: 'response' })
      .pipe(
        // Fail fast instead of hanging until the proxy's ~60s 504 when the backend is slow.
        timeout(AccountService.LOAD_TIMEOUT_MS),
        map((response) => {
          const loqtResponse = response.body;
          if (!loqtResponse) {
            console.warn("No response body for users");
            return [];
          }

          if (loqtResponse.statusCode?.status !== "OK") {
            console.warn("Unexpected users response status", loqtResponse.statusCode);
            return [];
          }

          const users = loqtResponse.body;
          if (!Array.isArray(users)) {
            console.warn("Invalid users payload", users);
            return [];
          }

          console.log("Users loaded successfully:", users);
          return users.map(buildSchoolUserFromResponse);
        }),
        catchError((error) => handleError<SchoolUser[]>("Loading users", error))
      );
  }

  /**
   * Loads a single user by sending a GET request to the `get-user/{accountId}` endpoint.
   *
   * Best-effort fast path (used by the header self-fetch so the avatar does not wait
   * for the full get-users roster): errors resolve to `undefined` instead of throwing,
   * because the roster subscription still populates the same data when it lands.
   *
   * @param {string} userAccountId - The account ID of the user to load.
   * @returns {Observable<SchoolUser | undefined>} The user, or `undefined` if unavailable.
   */
  loadUser(userAccountId: string): Observable<SchoolUser | undefined> {
    return this.http
      .get<DigiResponse<SchoolUserInfoResponse>>(buildApiEndpoint(this.SCHOOL_API_URI, `get-user/${userAccountId}`), { observe: 'response' })
      .pipe(
        timeout(AccountService.LOAD_TIMEOUT_MS),
        map((response) => {
          const loqtResponse = response.body;
          if (loqtResponse?.statusCode?.status !== "OK" || !loqtResponse.body) {
            console.warn("Unexpected user response status", loqtResponse?.statusCode);
            return undefined;
          }
          return buildSchoolUserFromResponse(loqtResponse.body);
        }),
        catchError((error) => {
          console.warn("Loading user failed:", error);
          return of(undefined);
        })
      );
  }

  /**
   * Adds a new user by sending a PUT request to the server.
   *
   * This function sends a PUT request to the `add-user` endpoint with the provided user data.
   * If the operation is successful, the response is mapped to a `SchoolUser` object using the `buildSchoolUserFromResponse` converter.
   * Logs the added user details and throws an error if no data is found in the response.
   *
   * @param {AddUserRequest} request - The request object containing the user data to be added.
   * @returns {Observable<SchoolUser>} An observable that emits the added `SchoolUser` object or throws an error if the operation fails.
   */
  addUser(request: AddUserRequest): Observable<SchoolUser> {
    return this.http
      .post<DigiResponse<SchoolUserInfoResponse>>(buildApiEndpoint(this.SCHOOL_API_URI, "add-user"), request, { observe: 'response' })
      .pipe(
        map((response) => {
          const loqtResponse = response.body!;
          if (loqtResponse.statusCode.status === "OK" && loqtResponse.body) {
            console.log("User added successfully:", loqtResponse.body);
            return buildSchoolUserFromResponse(loqtResponse.body);
          }
          const errorMessage = loqtResponse.statusCode.reason ? loqtResponse.statusCode.reason : "No user data found in response";
          throw new Error(errorMessage);
        }),
        catchError((error) => handleError<SchoolUser>("Adding user", error))
      );
  }

  /**
   * Updates an existing user by sending a PUT request to the server.
   *
   * This function sends a PUT request to the `modify-user` endpoint with the provided user data.
   * If the operation is successful, the response is mapped to a `SchoolUser` object using the `buildSchoolUserFromResponse` converter.
   * Logs the updated user details and throws an error if no data is found in the response.
   *
   * @param {ModifyUserRequest} request - The request object containing the user data to be updated.
   * @returns {Observable<SchoolUser>} An observable that emits the updated `SchoolUser` object or throws an error if the operation fails.
   */
  updateUser(request: ModifyUserRequest): Observable<SchoolUser> {
    return this.http
      .put<DigiResponse<SchoolUserInfoResponse>>(buildApiEndpoint(this.SCHOOL_API_URI, "modify-user"), request, { observe: 'response' })
      .pipe(
        map((response) => {
          const loqtResponse = response.body!;
          if (loqtResponse.statusCode.status === "OK" && loqtResponse.body) {
            console.log("User updated successfully:", loqtResponse.body);
            return buildSchoolUserFromResponse(loqtResponse.body);
          }
          throw new Error("No user data found in response");
        }),
        catchError((error) => handleError<SchoolUser>("Updating user", error))
      );
  }

  /**
   * Removes a user by sending a DELETE request to the server.
   *
   * This function sends a DELETE request to the `remove-user` endpoint with the specified user account ID.
   * If the operation is successful, the response is mapped to the user account ID.
   * Logs the removed user account ID and throws an error if no data is found in the response.
   *
   * @param {string} userAccountId - The ID of the user account to be removed.
   * @returns {Observable<string>} An observable that emits the removed user account ID or throws an error if the operation fails.
   */
  removeUser(userAccountId: string): Observable<string> {
    return this.http
      .delete<DigiResponse<string>>(buildApiEndpoint(this.SCHOOL_API_URI, `remove-user/${userAccountId}`), { observe: 'response' })
      .pipe(
        map((response) => {
          const loqtResponse = response.body!;
          if (loqtResponse.statusCode.status === "OK" && loqtResponse.body) {
            console.log("User removed successfully:", loqtResponse.body);
            return loqtResponse.body;
          }
          throw new Error("No user data found in response");
        }),
        catchError((error) => handleError<string>("Removing user", error))
      );
  }

  /**
   * Updates an existing user profile by sending a POST request to the server.
   *
   * This function sends a POST request to the `update-profile` endpoint with the provided user profile data.
   * If the operation is successful, the response is mapped to a `UserProfile` object using the `buildUserProfileFromResponse` converter.
   * Logs the updated user details and throws an error if no data is found in the response.
   *
   * @param {ModifyUserProfileRequest} request - The request object containing the user profile data to be updated.
   * @returns {Observable<UserProfile>} An observable that emits the updated `UserProfile` object or throws an error if the operation fails.
   */
  updateUserProfile(request: ModifyUserProfileRequest): Observable<UserProfile> {
    return this.http
      .post<DigiResponse<string>>(buildApiEndpoint(this.ACCOUNT_API_URI, "update-profile"), request, { observe: 'response' })
      .pipe(
        map((response) => {
          const loqtResponse = response.body!;
          if (loqtResponse.statusCode.status === "OK" && loqtResponse.body) {
            console.log("User profile updated successfully:", loqtResponse.body);
            return buildUserProfileFromRequest(request);
          }
          throw new Error("No user data found in response");
        }),
        catchError((error) => handleError<UserProfile>("Updating user profile", error))
      );
  }

  /**
   * Loads school information by sending a GET request to the server.
   *
   * This function retrieves school information for the specified school account ID by making a GET request to the `get-school-info` endpoint.
   * The response is mapped to a `School` object using the `buildSchoolFromResponse` converter.
   * Logs a warning if no school information is found for the given ID and returns undefined in such cases.
   *
   * @param {string} schoolAccountId - The ID of the school account whose information is to be loaded.
   * @returns {Observable<School | undefined>} An observable that emits the `School` object or undefined if no school information is found.
   */
  /**
   * Renames the school by sending a PUT request to the `rename-school` endpoint.
   *
   * On success the server returns the new school name; this method emits it so the store
   * can update the cached school info. Throws an error if the operation fails.
   *
   * @param {ModifySchoolRequest} request - The request containing the school account ID and the new name.
   * @returns {Observable<string>} An observable that emits the updated school name.
   */
  updateSchool(request: ModifySchoolRequest): Observable<string> {
    return this.http
      .put<DigiResponse<string>>(buildApiEndpoint(this.SCHOOL_API_URI, "rename-school"), request, { observe: 'response' })
      .pipe(
        map((response) => {
          const loqtResponse = response.body!;
          if (loqtResponse.statusCode.status === "OK") {
            console.log("School renamed successfully:", request.schoolName);
            return request.schoolName;
          }
          const errorMessage = loqtResponse.statusCode.reason ? loqtResponse.statusCode.reason : "School could not be renamed";
          throw new Error(errorMessage);
        }),
        catchError((error) => handleError<string>("Renaming school", error))
      );
  }

  /**
   * Updates the school's contact information (phone numbers and addresses) via a PUT request
   * to the `update-contact` endpoint. On success the request is echoed back so the store can
   * update the cached school info. Throws an error if the operation fails.
   *
   * @param {UpdateSchoolContactRequest} request - The contact update request.
   * @returns {Observable<UpdateSchoolContactRequest>} An observable that emits the applied request.
   */
  updateSchoolContact(request: UpdateSchoolContactRequest): Observable<UpdateSchoolContactRequest> {
    return this.http
      .put<DigiResponse<string>>(buildApiEndpoint(this.SCHOOL_API_URI, "update-contact"), request, { observe: 'response' })
      .pipe(
        map((response) => {
          const loqtResponse = response.body!;
          if (loqtResponse.statusCode.status === "OK") {
            console.log("School contact updated successfully");
            return request;
          }
          const errorMessage = loqtResponse.statusCode.reason ? loqtResponse.statusCode.reason : "School contact could not be updated";
          throw new Error(errorMessage);
        }),
        catchError((error) => handleError<UpdateSchoolContactRequest>("Updating school contact", error))
      );
  }

  /**
   * Sets or removes the school's profile photo via the `update-logo` endpoint. On success the
   * photo is echoed back so the store can update the cached school info.
   *
   * @param {UpdateSchoolLogoRequest} request - The photo update request; an empty photo removes it.
   * @returns {Observable<string>} An observable that emits the applied photo.
   */
  updateSchoolLogo(request: UpdateSchoolLogoRequest): Observable<string> {
    return this.http
      .put<DigiResponse<string>>(buildApiEndpoint(this.SCHOOL_API_URI, "update-logo"), request, { observe: 'response' })
      .pipe(
        map((response) => {
          const loqtResponse = response.body!;
          if (loqtResponse.statusCode.status === "OK") {
            console.log("School photo updated successfully");
            return request.logo;
          }
          const errorMessage = loqtResponse.statusCode.reason ? loqtResponse.statusCode.reason : "School photo could not be updated";
          throw new Error(errorMessage);
        }),
        catchError((error) => handleError<string>("Updating school photo", error))
      );
  }

  loadSchoolInfo(schoolAccountId: string): Observable<School | undefined> {
    return this.http
      .get<DigiResponse<SchoolResponse>>(buildApiEndpoint(this.SCHOOL_API_URI, `get-school-info/${schoolAccountId}`), { observe: 'response' })
      .pipe(
        map((response) => {
          const loqtResponse = response.body;
          if (!loqtResponse) {
            throw new Error("No response body for school info for ID: " + schoolAccountId);
          }

          if (loqtResponse.statusCode?.status !== "OK") {
            const status = loqtResponse.statusCode?.status ?? "UNKNOWN";
            const reason = loqtResponse.statusCode?.reason ?? "No reason provided";
            throw new Error(`Unexpected school info response (status: ${status}): ${reason}`);
          }

          const schoolResp = loqtResponse.body;
          if (!schoolResp) {
            throw new Error("No school info found for ID: " + schoolAccountId);
          }

          console.log("School info loaded successfully:", schoolResp);
          return buildSchoolFromResponse(schoolResp);
        }),
        catchError((error) => handleError<School | undefined>("Loading school info", error))
      );
  }

  loadSchoolInfoFromIntegration(schoolId: string): Observable<School | undefined> {
    return this.http
      .get<DigiResponse<SchoolIntegrationResponse>>(buildApiEndpoint(this.INTEGRATION_API_URI, `${schoolId}`), { observe: 'response' })
      .pipe(
        map((response) => {
          const loqtResponse = response.body;
          if (!loqtResponse) {
            throw new Error("No response body for school info for ID: " + schoolId);
          }

          if (loqtResponse.statusCode?.status !== "OK") {
            const status = loqtResponse.statusCode?.status ?? "UNKNOWN";
            const reason = loqtResponse.statusCode?.reason ?? "No reason provided";
            throw new Error(`Unexpected school info response from integration (status: ${status}): ${reason}`);
          }

          const schoolResp = loqtResponse.body;
          if (!schoolResp) {
            throw new Error("No school info found for ID: " + schoolId);
          }

          console.log("School info loaded successfully:", schoolResp);
          return buildSchoolFromIntegrationResponse(schoolResp);
        }),
        catchError((error) => handleError<School | undefined>("Loading school info", error))
      );
  }

  /**
   * Asks the backend to start a synchronization. It answers as soon as the run has started, not
   * when it finishes: a sync takes minutes, and it has to survive the page being left or closed.
   * Progress and the result are read from {@link getSyncStatus}.
   */
  syncSchoolAndStudentsFromIntegration(schoolId: string, schoolAccountId: string, confirmLargeRemovals = false, syncMode?: SyncMode): Observable<void> {
    // confirmLargeRemovals is only sent when the admin pressed Sync anyway: it lets the run take
    // more pupils out of a task than the backend allows without asking.
    const request = {
      schoolId,
      schoolAccountId,
      ...(confirmLargeRemovals && { confirmLargeRemovals: true }),
      ...(syncMode && { syncMode })
    };
    return this.http
      .post<DigiResponse<unknown>>(buildApiEndpoint(this.SCHOOL_API_URI, `sync-school-and-students`), request)
      .pipe(
        map((loqtResponse) => {
          if (loqtResponse?.statusCode?.status !== "OK") {
            throw new Error("Failed to start the synchronization: " + loqtResponse?.statusCode?.reason);
          }
          return undefined;
        }),
        catchError((error) => handleError<void>("Starting the school synchronization", error))
      );
  }

  /**
   * Takes the caller's school off LOQT (DIGI-1855). The backend reads the school from the token,
   * checks the caller administers it and that the typed name is the school's; a refusal comes back
   * as its own sentence ("The school name does not match.") so the page can show it as it is.
   */
  deactivateSchool(confirmSchoolName: string): Observable<void> {
    return this.http
      .post<DigiResponse<string>>(buildApiEndpoint(this.SCHOOL_API_URI, "deactivate"), { confirmSchoolName })
      .pipe(
        map((loqtResponse) => {
          if (loqtResponse?.statusCode?.status !== "OK") {
            throw new Error(loqtResponse?.statusCode?.reason || "The school account could not be deactivated. Please try again.");
          }
        }),
        catchError((error) => handleError<void>("Deactivating the school", error))
      );
  }

  /**
   * Saves how the MIS sync uses the school's data (year groups, classes or both). A refusal comes
   * back as the backend's own sentence (for instance the tasks that still target groups the new
   * mode would remove), so the page can show it as it is.
   */
  setSyncMode(schoolAccountId: string, syncMode: SyncMode): Observable<SyncMode> {
    return this.http
      .post<DigiResponse<{ syncMode?: string | null }>>(buildApiEndpoint(this.SCHOOL_API_URI, `integration/sync-mode`), { schoolAccountId, syncMode })
      .pipe(
        map((loqtResponse) => {
          if (loqtResponse?.statusCode?.status !== "OK") {
            throw new Error(loqtResponse?.statusCode?.reason || "The sync mode could not be saved.");
          }
          return toSyncMode(loqtResponse.body?.syncMode) ?? syncMode;
        }),
        catchError((error) => handleError<SyncMode>("Saving the sync mode", error))
      );
  }

  /** Whether a synchronization is still running for the school, and what the last one produced. */
  getSyncStatus(schoolAccountId: string): Observable<SchoolSyncStatusResponse> {
    return this.http
      .get<DigiResponse<SchoolSyncStatusResponse>>(buildApiEndpoint(this.SCHOOL_API_URI, `sync-status/${schoolAccountId}`))
      .pipe(
        map((loqtResponse) => {
          if (loqtResponse?.statusCode?.status !== "OK" || !loqtResponse.body) {
            throw new Error("Failed to read the synchronization status: " + loqtResponse?.statusCode?.reason);
          }
          return loqtResponse.body;
        }),
        catchError((error) => handleError<SchoolSyncStatusResponse>("Reading the synchronization status", error))
      );
  }

}
